import { useEffect, useState } from 'react';
import { FileCode } from 'lucide-react';
import DynamicProgressModal from '../DynamicProgressModal/view';
import XmlUploader from '../../molecules/XmlUploader/view';
import './style.css';
import ModalBtn from '../../molecules/modalButton/view';
import SpendRelater from '../../molecules/SpendRelater/view';
import ThirdsTable from '../thirdsTable/view';
import XmlPreview from '../PipelineDraftsManager/molecules/XmlPreview/view';
import ClientInfo from '../../atoms/ClientInfo/view';
import ServiceLineInput from '../../molecules/ServiceLineInput/view';
import PaymentMethodSelector from '../../molecules/PaymentMethodSelector/view';
import DateSelector from '../../atoms/datepicker/view';

import { dispatchCustomEvent, listener } from '../../../utils/dispatch';
import { createSpend, createSpendWithSupportDocument } from '../../../usecases/spend-usecase';
import { formatMoney } from '../../../utils/formating';
import { CheckCircle, RadioButtonUnchecked, FiberManualRecord } from '@mui/icons-material';

const SpendFromXml = ({ profile, hidden = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [invoiceData, setInvoiceData] = useState(null);
  const [selectedThird, setSelectedThird] = useState(null);
  const [relatedSpendLines, setRelatedSpendLines] = useState([]);
  const [selectedMethods, setSelectedMethods] = useState([]);
  const [selectedFormPay, setSelectedFormPay] = useState(undefined);
  const [spendDate, setSpendDate] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cálculos de totales de gasto
  const subtotal = relatedSpendLines.reduce((acc, line) => acc + (parseFloat(line.base) || parseFloat(line.amount) || 0), 0);
  const totalTax = relatedSpendLines.reduce((acc, line) => acc + (parseFloat(line.tax) || 0), 0);
  const totalRetention = relatedSpendLines.reduce((acc, line) => acc + (parseFloat(line.retention) || 0), 0);
  const totalAmount = subtotal + totalTax - totalRetention;

  useEffect(() => {
    if (invoiceData?.issueDate) {
      const parsedDate = new Date(invoiceData.issueDate + 'T12:00:00Z');
      if (!isNaN(parsedDate.getTime())) {
        setSpendDate(parsedDate);
      }
    }
  }, [invoiceData?.issueDate]);

  useEffect(() => {
    // Al seleccionar tercero en la tabla, avanzar al paso de Pago / Split View
    const cleanThird = listener("btn-thirds-add", (e) => {
      if (e.detail?.data) {
        setSelectedThird(e.detail.data);
        setCurrentStep(3); // Paso 4
      }
    });
    const cleanOpenWithData = listener("open-spend-from-xml-with-data", (e) => {
      const data = e.detail?.data;
      if (data) {
        setInvoiceData(data);

        if (data.prefilled) {
          // Viene del Auxiliar Contable con datos ya procesados
          if (data.serviceLines?.length) {
            setRelatedSpendLines(data.serviceLines);
          }
          if (data.selectedMethods?.length) {
            setSelectedMethods(data.selectedMethods);
          }
          if (data.tercero) {
            setSelectedThird(data.tercero);
          }
          setCurrentStep(0); // Iniciar precargado en el Paso 1 (Carga/Resumen XML)
        } else {
          setCurrentStep(1); // Avanzar al paso de relacionar tipos de gasto
        }

        // Abrir el modal programáticamente
        dispatchCustomEvent('modalspendfromxml-btn', { eventKey: 'spendfromxml-btn' });
      }
    });
    return () => {
      cleanThird();
      cleanOpenWithData();
    };
  }, []);

  const handleSpendTypesMatch = (items) => {
    const mappedLines = items.map((item, index) => {
      const typeData = item.relatedItem || {};
      const amountVal = parseFloat(item.total || item.subtotal || item.amount || 0);
      return {
        id: Date.now() + index,
        spend_type_id: typeData.id,
        spend_type_data: typeData,
        xmlDescription: item.description || item.name || 'Gasto XML',
        matchedType: typeData.name || 'Sin tipo',
        amount: amountVal,
        description: item.description,
        tax: 0,
        base: amountVal,
        retention: 0
      };
    });
    setRelatedSpendLines(mappedLines);
    setCurrentStep(2); // Avanzar a Confirmar Proveedor
  };

  const handleServiceLine = (lines) => {
    setRelatedSpendLines(lines);
  };

  async function handleCreateSpend(isDocSupport = false) {
    if (!selectedThird) {
      dispatchCustomEvent("popup", { message: "Debe seleccionar un proveedor.", type: "warning" });
      return;
    }
    if (relatedSpendLines.length === 0) {
      dispatchCustomEvent("popup", { message: "Debe incluir al menos una línea de gasto.", type: "warning" });
      return;
    }
    if (selectedMethods.length === 0) {
      dispatchCustomEvent("popup", { message: "Debe agregar al menos un medio de pago.", type: "warning" });
      return;
    }
    if (isDocSupport && !selectedFormPay) {
      dispatchCustomEvent("popup", { message: "Debe seleccionar la forma de pago (Contado/Crédito).", type: "warning" });
      return;
    }

    const xmlInvoiceDataPayload = invoiceData ? {
      cufe: invoiceData.cufe || null,
      invoice_number: invoiceData.id || null,
      supplier_nit: invoiceData.supplierNIT || null,
      supplier_name: invoiceData.supplier || null,
      supplier_email: invoiceData.supplierEmail || null,
      payment_due_date: invoiceData.paymentDueDate || null,
      payment_means_code: invoiceData.paymentMeansCode || null,
    } : null;

    const payload = {
      tercero: selectedThird,
      service_lines: relatedSpendLines,
      payment_methods: selectedMethods.map(method => ({
        ...method,
        payment_form_id: isDocSupport ? (parseInt(selectedFormPay) || 1) : undefined
      })),
      retention: totalRetention,
      spend_date: spendDate,
      xml_invoice_data: xmlInvoiceDataPayload ? JSON.stringify(xmlInvoiceDataPayload) : null
    };

    setIsSubmitting(true);
    try {
      if (isDocSupport) {
        await createSpendWithSupportDocument(payload);
      } else {
        await createSpend(payload);
      }
      if (invoiceData?.draft_id) {
        dispatchCustomEvent("discard-pipeline-draft", { id: invoiceData.draft_id });
      }
      dispatchCustomEvent("popup", { message: "Gasto creado con éxito desde XML", type: "success" });
      dispatchCustomEvent("reload-spends", {});
      dispatchCustomEvent("reload-pipeline-drafts", {});
      dispatchCustomEvent("modal-close", null);
      setTimeout(() => {
        setCurrentStep(0);
        setInvoiceData(null);
        setSelectedThird(null);
        setRelatedSpendLines([]);
        setSelectedMethods([]);
        setSelectedFormPay(undefined);
        setIsSubmitting(false);
      }, 300);
    } catch (error) {
      console.error("[SpendFromXml] Error creating spend:", error);
      const err = error?.response?.data?.error || error?.response?.data?.message;
      const messages = {
        "bad_request": "Solicitud inválida. Verifique los datos.",
        "Error creando gasto debido a un problema de integridad de datos.": "Error de integridad de datos al crear el gasto.",
      };
      const displayMessage = messages[err] || err || error?.message || "Error al crear el gasto";
      dispatchCustomEvent("popup", { message: displayMessage, type: "error" });
      setIsSubmitting(false);
    }
  }

  const steps = [
    {
      title: 'Cargar factura XML',
      component: () => (
        <div style={{ maxHeight: '60vh', width: '100%', overflowY: "auto" }}>
          <XmlUploader handleData={setInvoiceData} invoiceData={invoiceData} />
        </div>
      ),
      showContinueButton: !!invoiceData,
    },
    {
      title: 'Relacionar Tipos de Gasto',
      component: () => (
        <div style={{ maxHeight: '60vh', width: '100%', overflowY: "auto" }}>
          <SpendRelater
            array1={invoiceData?.items || []}
            handleData={handleSpendTypesMatch}
          />
        </div>
      ),
      showContinueButton: false,
    },
    {
      title: 'Confirmar Proveedor',
      component: () => (
        <div className="thirds-step-container">
          {invoiceData && (
            <div
              className="xml-supplier-info-card"
              style={{
                backgroundColor: 'var(--color-surface, #1e293b)',
                border: '1px solid var(--color-border, #334155)',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: '11px',
                    color: 'var(--color-text-muted, #94a3b8)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '600',
                  }}
                >
                  Proveedor extraído de la factura XML:
                </span>
                <div
                  style={{
                    fontSize: '14px',
                    fontWeight: '700',
                    color: 'var(--color-text, #f8fafc)',
                    marginTop: '2px',
                  }}
                >
                  {invoiceData.supplier || 'Proveedor sin nombre'}{' '}
                  <span style={{ fontWeight: '400', opacity: 0.85 }}>
                    ({invoiceData.supplierNIT ? `NIT: ${invoiceData.supplierNIT}` : 'Sin NIT'})
                  </span>
                </div>
              </div>
            </div>
          )}
          <ThirdsTable
            id="spendfromxml-thirdstable"
            mode="selector"
            forceLoadData={true}
            profile={profile}
            maxHeight="300px"
            filter={invoiceData?.supplierNIT}
            initialThird={
              invoiceData?.supplierNIT ? {
                identification: invoiceData.supplierNIT,
                name: invoiceData.supplier,
                email: invoiceData.supplierEmail,
                phone: invoiceData.supplierPhone
              } : {}
            }
          />
        </div>
      ),
      showContinueButton: false,
    },
    {
      title: 'Confirmación y Pago de Gasto',
      component: () => (
        <div style={{ height: 'calc(68vh - 40px)', width: '100%', overflow: 'hidden', paddingBottom: '10px' }}>
          <div className="row h-100 g-3">
            {/* Panel Izquierdo (60%): Resumen XML y Líneas Comparativas de Gasto */}
            <div className="col-12 col-lg-7 h-100 d-flex flex-column" style={{ borderRight: '1px solid var(--color-border, #334155)', paddingRight: '15px' }}>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {invoiceData?.rawContent ? (
                  <XmlPreview base64Data={btoa(unescape(encodeURIComponent(invoiceData.rawContent)))} />
                ) : (
                  <div className="p-3 rounded mb-3" style={{ background: 'var(--color-surface, #1e293b)', border: '1px solid var(--color-border, #334155)' }}>
                    <h6 className="fw-bold mb-2 text-primary" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>📄 DATOS EXTRAÍDOS DEL XML</h6>
                    <div className="d-flex flex-column gap-2" style={{ fontSize: '13px' }}>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Documento:</span>
                        <strong className="text-light">{invoiceData?.id || 'N/A'}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Fecha Emisión:</span>
                        <strong className="text-light">{invoiceData?.issueDate ? String(invoiceData.issueDate).substring(0, 10) : 'N/A'}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">CUFE:</span>
                        <strong className="text-truncate text-light" style={{ maxWidth: '200px' }} title={invoiceData?.cufe}>{invoiceData?.cufe || 'N/A'}</strong>
                      </div>
                    </div>
                  </div>
                )}
                {/* Resumen Comparativo de Líneas de Gasto */}
                <div className="p-3 rounded mt-2" style={{ background: 'var(--color-surface, #1e293b)', border: '1px solid var(--color-border, #334155)' }}>
                  <h6 className="fw-bold mb-2 text-success" style={{ fontSize: '13px', letterSpacing: '0.5px' }}>📦 SERVICIOS / GASTOS MATCH ({relatedSpendLines.length})</h6>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {relatedSpendLines.map((item, idx) => (
                      <div key={idx} className="border-bottom py-2" style={{ fontSize: '12px', borderColor: 'var(--color-border, #334155)' }}>
                        <div className="d-flex justify-content-between text-muted" style={{ fontSize: '11px' }}>
                          <span>XML: {item.xmlDescription || item.description}</span>
                          <span>Base: {formatMoney(item.base || item.amount)}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <strong className="text-info text-truncate" style={{ maxWidth: '70%' }}>
                            ➜ GASTO: {item.matchedType || item.spend_type_data?.name || 'Tipo de Gasto'}
                          </strong>
                          <strong className="text-light">{formatMoney(item.amount)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Panel Derecho (40%): Interfaz de Formulario de Gasto / Pago Reutilizada */}
            <div className="col-12 col-lg-5 h-100 d-flex flex-column" style={{ overflowY: 'auto', paddingLeft: '15px', paddingRight: '5px', paddingBottom: '10px' }}>
              <div className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="text-muted" style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: 600 }}>PROVEEDOR CONFIRMADO</span>
                  <span className="fw-bold text-light" style={{ fontSize: '16px' }}>Total: {formatMoney(totalAmount)}</span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <DateSelector
                    label="Fecha del Gasto"
                    date={spendDate}
                    onChange={(date) => setSpendDate(date)}
                  />
                </div>
                <ClientInfo
                  tercero={selectedThird || { name: invoiceData?.supplier, identification: invoiceData?.supplierNIT }}
                  type="provider"
                  notActions={true}
                />
              </div>

              <div className="mb-2">
                <ServiceLineInput
                  onServiceLinesChange={handleServiceLine}
                  serviceLines={relatedSpendLines}
                />
              </div>

              <div className="spend-summary-box my-2 p-2 rounded" style={{ background: 'var(--color-surface, #1e293b)', fontSize: '12px' }}>
                <div className="d-flex justify-content-between"><span>Total Bruto:</span> <span>{formatMoney(subtotal)}</span></div>
                <div className="d-flex justify-content-between"><span>Impuestos:</span> <span>{formatMoney(totalTax)}</span></div>
                <div className="d-flex justify-content-between" style={{ color: 'red' }}><span>Retenciones:</span> <span>-{formatMoney(totalRetention)}</span></div>
                <hr className="my-1" />
                <div className="d-flex justify-content-between fw-bold"><span>Total a Pagar:</span> <span className="text-success">{formatMoney(totalAmount)}</span></div>
              </div>

              <div style={{ flex: 1 }}>
                <PaymentMethodSelector
                  id="spendfromxml-payment-methods"
                  selectedMethods={selectedMethods}
                  onSelectMethod={setSelectedMethods}
                  onAmountChange={setSelectedMethods}
                  onRemoveMethod={setSelectedMethods}
                  enableFormPay={true}
                  selectedFormPay={setSelectedFormPay}
                  selectedFormPayValue={selectedFormPay}
                  defaultAmount={totalAmount}
                />
              </div>

              <div className="d-flex justify-content-end mt-2 pt-2 border-top" style={{ borderColor: 'var(--color-border, #334155)' }}>
                <button
                  onClick={() => handleCreateSpend(false)}
                  disabled={relatedSpendLines.length === 0 || !selectedThird || selectedMethods.length === 0 || isSubmitting}
                  className="btn btn-primary px-4 py-2"
                  style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-primary, #0d6efd)',
                    borderColor: 'var(--color-primary, #0d6efd)',
                    minWidth: '200px'
                  }}
                >
                  {isSubmitting ? 'Guardando...' : 'Finalizar Gasto'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ),
      showContinueButton: false,
    }
  ];

  const CustomStepper = () => (
    <div className="custom-stepper">
      {['Carga', 'Vínculo', 'Proveedor', 'Pago'].map((label, idx) => (
        <div key={label} className={`step-item ${currentStep === idx ? 'active' : ''} ${currentStep > idx ? 'completed' : ''}`} onClick={() => setCurrentStep(idx)} style={{ cursor: 'pointer' }}>
          <div className="step-icon">
            {currentStep > idx ? <CheckCircle /> : currentStep === idx ? <FiberManualRecord /> : <RadioButtonUnchecked />}
          </div>
          <span className="step-label">{label}</span>
          {idx < 3 && <div className="step-line" />}
        </div>
      ))}
    </div>
  );

  return (
    <ModalBtn id="spendfromxml-btn" title="Cargar Gasto desde XML" icon={<FileCode size={20} />} hidden={hidden} style={{ width: '90vw', height: '90vh' }}>
      <div className='spend-form-xml-content' style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
        <CustomStepper />
        <DynamicProgressModal
          currentStepIndex={currentStep}
          onStepChange={setCurrentStep}
          steps={steps}
          onFinish={() => {
            /* noop */
          }}
          hiddenBar={true}
          hiddenTitle={true}
        />
      </div>
    </ModalBtn>
  );
};

export default SpendFromXml;
